import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { loadPersistedState, persistState } from '../../utils/uiStatePersistence';
import { DEFAULT_MONTAGE_TYPE, DEFAULT_ZSCORE_VISUALIZATION_SETTINGS } from '../constants';


// Create a context for sharing patient z-scores data
export const PatientZScoresContext = createContext([]);


// Provider component for the context
export const PatientZScoresProvider = ({ children }) => {
    // Restore the persisted z-score view state once on mount, falling back to
    // the current defaults when nothing (or invalid data) is stored.
    const [persistedInitial] = useState(() => loadPersistedState());
    const [patientZScores, setPatientZScores] = useState([]);
    const [montageType, setMontageType] = useState(() => (
        persistedInitial.montageType ?? DEFAULT_MONTAGE_TYPE
    ));
    const [zScoreVisualizationSettings, setZScoreVisualizationSettings] = useState(() => ({
        ...DEFAULT_ZSCORE_VISUALIZATION_SETTINGS,
        ...(persistedInitial.zScoreVisualizationSettings || {})
    }));

    // Persist the z-score view state whenever it changes.
    useEffect(() => {
        persistState({ montageType, zScoreVisualizationSettings });
    }, [montageType, zScoreVisualizationSettings]);

    // Debug state changes
    useEffect(() => {
        console.log("PatientZScoresProvider state updated:", patientZScores);
    }, [patientZScores]);

    useEffect(() => {
        console.log("Z-Score visualization settings updated:", zScoreVisualizationSettings);
    }, [zScoreVisualizationSettings]);

    const contextValue = useMemo(() => ({
        patientZScores,
        setPatientZScores,
        montageType,
        setMontageType,
        zScoreVisualizationSettings,
        setZScoreVisualizationSettings
    }), [patientZScores, montageType, zScoreVisualizationSettings]);

    return (
        <PatientZScoresContext.Provider value={contextValue}>
            {children}
        </PatientZScoresContext.Provider>
    );
};


// Hook to access the patient z-scores context
export const usePatientZScores = () => useContext(PatientZScoresContext);
